import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  ClipboardList,
  Copy,
  Edit3,
  IndianRupee,
  Landmark,
  Link2,
  ListChecks,
  Loader2,
  MapPin,
  Share2,
  Trophy,
  Users,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/modules';
import {
  Button,
  Card,
  EmptyState,
  InfoBanner,
  PageTabRow,
  StatusPill,
} from '../../components/ui';
import { getPublicWebBaseUrl } from '../../utils/publicWebBaseUrl';
import { DAY_NAMES } from './tournamentFormUtils';
import { getTournament } from './tournamentApi';
import TournamentRegistrationsPanel from './TournamentRegistrationsPanel';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Trophy },
  { id: 'registrations', label: 'Registrations', icon: Users },
  { id: 'fixtures', label: 'Fixtures', icon: Calendar },
  { id: 'financials', label: 'Financials', icon: Landmark },
];

const formatDate = (value) => value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  : 'Not set';

const statusPill = (status) => ({
  status: status === 'ongoing' ? 'confirmed' : status === 'upcoming' ? 'pending' : status,
  label: status ? `${status[0].toUpperCase()}${status.slice(1)}` : 'Draft',
});

function DetailItem({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-ga-text-primary">{children}</dd>
    </div>
  );
}

function CopySection({ title, value, icon: Icon }) {
  if (!value) return null;
  return (
    <Card>
      <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
        <Icon className="h-4 w-4" /> {title}
      </h2>
      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-ga-text-primary">{value}</p>
    </Card>
  );
}

export const getTournamentPublicLink = (tournamentId) =>
  `${getPublicWebBaseUrl()}/enroll/${tournamentId}`;

export default function TournamentDetail() {
  const { tournamentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission(MODULES.TOURNAMENTS, 'write');
  const requestedTab = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab.id === requestedTab) ? requestedTab : 'overview';
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const requestIdRef = useRef(0);

  const loadTournament = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const payload = await getTournament(tournamentId);
      if (requestId === requestIdRef.current) setTournament(payload.data);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setTournament(null);
        setError(err.message || 'Failed to load tournament');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    loadTournament();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadTournament]);

  const publicLink = useMemo(
    () => getTournamentPublicLink(tournamentId),
    [tournamentId]
  );
  const detailTabs = useMemo(
    () => TABS.map((tab) => (
      tab.id === 'registrations'
        ? { ...tab, badge: Number(tournament?.pending_registration_count || 0) }
        : tab
    )),
    [tournament?.pending_registration_count]
  );

  const changeTab = (tab) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };

  const copyPublicLink = async () => {
    await navigator.clipboard.writeText(publicLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Link
          to="/dashboard/tournaments"
          aria-label="Back to tournaments"
          className="mb-4 inline-flex items-center gap-2 rounded-lg p-2 text-sm font-semibold text-ga-text-primary hover:bg-ga-bg-surface-subtle"
        >
          <ArrowLeft className="h-5 w-5" /> Tournaments
        </Link>

        {error && <InfoBanner variant="danger" className="mb-4">{error}</InfoBanner>}

        {loading ? (
          <div className="py-16 text-center text-sm text-ga-text-muted">
            <Loader2 className="mx-auto mb-2 h-7 w-7 animate-spin" />
            Loading tournament…
          </div>
        ) : tournament && (
          <>
            <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-ga-text-primary">{tournament.name}</h1>
                  <StatusPill {...statusPill(tournament.lifecycle_status)} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-ga-text-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" /> {tournament.facility_name}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-4 w-4" /> {tournament.team_count || 0}
                    {tournament.max_teams ? ` / ${tournament.max_teams}` : ''} teams
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {canWrite && (
                  <Button
                    as={Link}
                    variant="ghost"
                    to={`/dashboard/tournaments/${tournament.id}/edit`}
                  >
                    <Edit3 className="h-4 w-4" /> Edit
                  </Button>
                )}
                <Button as="a" variant="ghost" href={publicLink} target="_blank" rel="noopener noreferrer">
                  <Share2 className="h-4 w-4" /> Share link
                </Button>
              </div>
            </div>

            <Card className="mb-5 p-0">
              <PageTabRow
                tabs={detailTabs}
                active={activeTab}
                onChange={changeTab}
                ariaLabel="Tournament sections"
              />
            </Card>

            {activeTab === 'overview' && (
              <div className="space-y-4">
                <Card>
                  <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
                    <ClipboardList className="h-4 w-4" /> Tournament details
                  </h2>
                  <dl className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    <DetailItem label="Registration window">
                      {formatDate(tournament.registration_starts_at)} – {formatDate(tournament.registration_ends_at)}
                    </DetailItem>
                    <DetailItem label="Match dates">
                      {formatDate(tournament.tournament_starts_at)} – {formatDate(tournament.tournament_ends_at)}
                    </DetailItem>
                    <DetailItem label="Format">
                      {tournament.overs_per_match ? `${tournament.overs_per_match} overs` : 'Not set'}
                      {tournament.ball_type ? ` · ${tournament.ball_type} ball` : ''}
                    </DetailItem>
                    <DetailItem label="Match day">
                      {DAY_NAMES[Number(tournament.match_day_of_week)] || 'Not set'}
                    </DetailItem>
                    <DetailItem label="Entry fee">
                      ₹{Number(tournament.registration_amount || 0).toLocaleString()}
                    </DetailItem>
                    <DetailItem label="Teams paid">
                      {tournament.paid_team_count || 0} / {tournament.team_count || 0}
                    </DetailItem>
                    <DetailItem label="League matches per team">
                      {tournament.matches_per_team || 'Not set'}
                    </DetailItem>
                    <DetailItem label="Playoff qualifiers">
                      {tournament.playoff_qualifiers || 'Not set'}
                    </DetailItem>
                  </dl>
                </Card>

                <Card>
                  <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
                    <Link2 className="h-4 w-4" /> Public registration link
                  </h2>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <code className="min-w-0 flex-1 break-all rounded-ga-sm bg-ga-bg-surface-subtle px-3 py-2 text-xs text-ga-text-primary">
                      {publicLink}
                    </code>
                    <Button variant="ghost" onClick={copyPublicLink} className="text-xs">
                      <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </Card>

                <div className="grid gap-4 lg:grid-cols-3">
                  <CopySection title="Format" value={tournament.format_details} icon={Trophy} />
                  <CopySection title="Rules" value={tournament.rules_details} icon={ListChecks} />
                  <CopySection title="Prizes" value={tournament.prizes_details} icon={IndianRupee} />
                </div>
              </div>
            )}

            {activeTab === 'registrations' && (
              <TournamentRegistrationsPanel
                tournamentId={tournament.id}
                onChanged={loadTournament}
              />
            )}

            {activeTab === 'fixtures' && (
              <EmptyState
                card
                icon={Calendar}
                title="Fixtures aren't available yet"
                description="Match scheduling isn't set up for this tournament yet."
              />
            )}

            {activeTab === 'financials' && (
              <EmptyState
                card
                icon={Landmark}
                title="Financials aren't available yet"
                description="Payment and expense tracking isn't available yet."
              />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
